import { useEffect, useState } from 'react'
import type { TimeFixRequest } from '@shared/types'
import { get, post } from '../../lib/api'
import { formatDateTime, formatDuration, formatTime } from '../../lib/format'
import { useToast } from '../../state/toast'
import { Button, Field, SectionCard, Sheet } from '../../components/ui'

function span(inAt: string, outAt: string | null, tz: string): string {
  return `${formatDateTime(inAt, tz)} – ${outAt ? formatTime(outAt, tz) : 'still on'}`
}

function length(inAt: string, outAt: string | null): string {
  return outAt ? formatDuration(Date.parse(outAt) - Date.parse(inAt)) : ''
}

/**
 * The clock-out approving will write. A fix asked for mid-shift has none, and
 * approving keeps whatever clock-out the shift has by now.
 */
function approvedOut(request: TimeFixRequest): string | null {
  return request.clockOutAt ?? request.original?.clockOutAt ?? null
}

/**
 * Hour fixes the crew asked for. Approving applies exactly what they asked,
 * with the office's name and their reason on the entry; for anything else,
 * decline with a note or correct it on the Hours tab.
 */
export function FixRequests({ timezone, onChanged }: { timezone: string; onChanged: () => void }) {
  const toast = useToast()
  const [requests, setRequests] = useState<TimeFixRequest[]>([])
  const [acting, setActing] = useState<{ request: TimeFixRequest; approve: boolean } | null>(null)
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)

  const load = () => get<{ requests: TimeFixRequest[] }>('/api/admin/fix-requests')
    .then((res) => setRequests(res.requests))

  useEffect(() => {
    void load().catch(() => undefined)
    const id = setInterval(() => void load().catch(() => undefined), 60_000)
    return () => clearInterval(id)
  }, [])

  if (requests.length === 0) return null

  const submit = async () => {
    if (!acting) return
    setBusy(true)
    try {
      await post(`/api/admin/fix-requests/${acting.request.id}/${acting.approve ? 'approve' : 'decline'}`, {
        comment: comment.trim() || undefined,
      })
      toast.success(acting.approve ? 'Hours fixed' : 'Request declined')
      setActing(null)
      setComment('')
      await load()
      onChanged()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'That did not go through')
    } finally {
      setBusy(false)
    }
  }

  return (
    <SectionCard title={`Hours fix requests (${requests.length})`}>
      <div className="space-y-3">
        {requests.map((request) => (
          <div key={request.id} className="card border-l-4 border-l-brand p-3">
            <div className="flex items-baseline justify-between gap-2">
              <p className="truncate font-bold">{request.userName}</p>
              <span className="shrink-0 text-xs text-graphite-2">{request.entryId ? 'Fix' : 'Missing shift'}</span>
            </div>
            <p className="mt-1 text-sm">{request.reason}</p>
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
              {request.original && (
                <>
                  <dt className="text-graphite-2">Was</dt>
                  <dd className="font-mono tnum">
                    {request.original.siteName} · {span(request.original.clockInAt, request.original.clockOutAt, timezone)}
                  </dd>
                </>
              )}
              <dt className="font-bold">{request.original ? 'Change to' : 'Add'}</dt>
              <dd className="font-mono font-semibold tnum">
                {request.siteName} · {span(request.clockInAt, approvedOut(request), timezone)}
                {approvedOut(request) && ` (${length(request.clockInAt, approvedOut(request))})`}
              </dd>
            </dl>
            <div className="mt-3 flex gap-2">
              <Button className="min-h-10 flex-1 text-xs" onClick={() => setActing({ request, approve: false })}>
                Decline
              </Button>
              <Button variant="primary" className="min-h-10 flex-1 text-xs" onClick={() => setActing({ request, approve: true })}>
                Approve
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Sheet
        open={acting !== null}
        onClose={() => {
          setActing(null)
          setComment('')
        }}
        title={acting?.approve ? 'Approve the fix' : 'Decline the fix'}
        footer={
          <Button
            variant={acting?.approve ? 'primary' : 'secondary'}
            size="lg"
            className="w-full"
            loading={busy}
            onClick={() => void submit()}
          >
            {acting?.approve ? 'Approve and apply' : 'Decline'}
          </Button>
        }
      >
        <p className="mb-4 text-sm text-graphite-2">
          {acting?.approve
            ? `${acting.request.userName}'s times change to what they asked for, and they get a notification.`
            : `${acting?.request.userName}'s times stay as they are. They get a notification with your note.`}
        </p>
        <Field label="Note to them" hint="Optional">
          <textarea
            className="field"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={acting?.approve ? 'Fixed — thanks for flagging it.' : 'Your truck GPS shows you left at 3:10.'}
          />
        </Field>
      </Sheet>
    </SectionCard>
  )
}
